import { useEffect } from 'react'
import { SITE_NAME } from '../config/site'

type StructuredData = Record<string, unknown>

type PageMetadataProps = {
  title: string
  description: string
  image?: string
  imageAlt?: string
  url?: string
  robots?: string
  type?: 'website' | 'article'
  structuredData?: StructuredData[]
}

const upsertMeta = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, value)
    element.dataset.seoManaged = 'true'
    document.head.appendChild(element)
  }
  element.content = content
}

const setNamedMeta = (name: string, content: string) => (
  upsertMeta(`meta[name="${name}"]`, 'name', name, content)
)

const setPropertyMeta = (property: string, content: string) => (
  upsertMeta(`meta[property="${property}"]`, 'property', property, content)
)

export default function PageMetadata({
  title,
  description,
  image,
  imageAlt,
  url,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  type = 'website',
  structuredData = [],
}: PageMetadataProps) {
  useEffect(() => {
    document.title = title

    setNamedMeta('description', description)
    setNamedMeta('robots', robots)
    setNamedMeta('twitter:card', 'summary_large_image')
    setNamedMeta('twitter:title', title)
    setNamedMeta('twitter:description', description)
    setPropertyMeta('og:title', title)
    setPropertyMeta('og:description', description)
    setPropertyMeta('og:type', type)
    setPropertyMeta('og:site_name', SITE_NAME)
    setPropertyMeta('og:locale', 'en_US')

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (url) {
      const link = canonical || document.createElement('link')
      link.rel = 'canonical'
      link.href = url
      link.dataset.seoManaged = 'true'
      if (!canonical) document.head.appendChild(link)
      setPropertyMeta('og:url', url)
    } else {
      canonical?.remove()
      document.head.querySelector('meta[property="og:url"]')?.remove()
    }

    if (image) {
      setPropertyMeta('og:image', image)
      setNamedMeta('twitter:image', image)
      if (imageAlt) {
        setPropertyMeta('og:image:alt', imageAlt)
        setNamedMeta('twitter:image:alt', imageAlt)
      }
    } else {
      document.head.querySelector('meta[property="og:image"]')?.remove()
      document.head.querySelector('meta[property="og:image:alt"]')?.remove()
      document.head.querySelector('meta[name="twitter:image"]')?.remove()
      document.head.querySelector('meta[name="twitter:image:alt"]')?.remove()
    }

    let jsonLd = document.getElementById('site-structured-data') as HTMLScriptElement | null
    if (structuredData.length) {
      jsonLd ||= document.createElement('script')
      jsonLd.id = 'site-structured-data'
      jsonLd.type = 'application/ld+json'
      jsonLd.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': structuredData,
      }).replace(/</g, '\\u003c')
      if (!jsonLd.parentNode) document.head.appendChild(jsonLd)
    } else {
      jsonLd?.remove()
    }
  }, [description, image, imageAlt, robots, structuredData, title, type, url])

  return null
}
